import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Swal from 'sweetalert2'
import Loader from '../../components/Loader'
import './onboarding.css'

const EXPERIENCE_OPTIONS = [
  { value: 'experienced', label: 'Yes, I have experience with stock trading' },
  { value: 'some', label: 'No, I have little experience with stock trading' },
  { value: 'novice', label: 'Complete novice in stock trading' },
]

const GOAL_OPTIONS = ['Diversify my portfolio', 'Generate income', 'Grow long term wealth', 'Other']

const ASSET_OPTIONS = ['Stocks', 'Cryptocurrency', 'Forex', 'Commodities', 'Venture capital', 'Real estate']

const NET_WORTH_OPTIONS = [
  'Under $50,000',
  '$50,000 – $100,000',
  '$100,000 – $250,000',
  '$250,000 – $500,000',
  '$500,000 – $1,000,000',
  '$1,000,000 – $5,000,000',
  'Over $5,000,000',
  'Prefer not to say',
]

const ACCOUNT_TYPES = [
  { value: 'personal', label: 'Personal account', description: 'Best for everyone who trades with personal funds.' },
  { value: 'corporate', label: 'Corporate (for expert traders)', description: 'Best for experts who trade with company funds.' },
]

const CURRENCIES = [
  { value: 'USD', label: 'USD — United States Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'CHF', label: 'CHF — Swiss Franc' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
]

const TOTAL_STEPS = 7

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
})

const Onboarding = ({ route }) => {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loader, setLoader] = useState(true)
  const [answers, setAnswers] = useState({
    investmentExperience: '',
    investmentGoals: [],
    assetInterests: [],
    netWorth: '',
    accountType: '',
    accountCurrency: 'USD',
    firstname: '',
    lastname: '',
    address: '',
    state: '',
    city: '',
    zipcode: '',
    country: '',
  })

  // Guard the page and prefill details the user already gave at signup
  useEffect(() => {
    const getData = async () => {
      const token = localStorage.getItem('token')
      if (!token) { navigate('/login'); return }
      try {
        const response = await fetch(`${route}/api/getData`, {
          headers: { 'x-access-token': token, 'Content-Type': 'application/json' },
        })
        const data = await response.json()
        if (data.status === 'error') {
          localStorage.removeItem('token')
          navigate('/login')
          return
        }
        if (data.onboardingCompleted !== false) {
          navigate('/dashboard')
          return
        }
        setAnswers(prev => ({
          ...prev,
          firstname: data.firstname || '',
          lastname: data.lastname || '',
          address: data.address || '',
          state: data.state || '',
          city: data.city || '',
          zipcode: data.zipcode || '',
          country: data.country || '',
        }))
      } catch (error) {
        navigate('/login')
      } finally {
        setLoader(false)
      }
    }
    getData()
  }, [navigate, route])

  const setField = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }))

  const toggleInList = (key, value) => setAnswers(prev => ({
    ...prev,
    [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value],
  }))

  const stepIsValid = () => {
    switch (step) {
      case 2: return answers.investmentExperience !== ''
      case 3: return answers.investmentGoals.length > 0
      case 4: return answers.assetInterests.length > 0
      case 5: return answers.netWorth !== ''
      case 6: return answers.accountType !== ''
      default: return true
    }
  }

  const next = () => {
    if (!stepIsValid()) {
      Toast.fire({ icon: 'warning', title: 'Please choose an option to continue' })
      return
    }
    setStep(s => Math.min(s + 1, TOTAL_STEPS))
  }

  const previous = () => setStep(s => Math.max(s - 1, 1))

  const submit = async (e) => {
    e.preventDefault()
    setLoader(true)
    try {
      const response = await fetch(`${route}/api/completeOnboarding`, {
        method: 'POST',
        headers: { 'x-access-token': localStorage.getItem('token'), 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      })
      const result = await response.json()
      if (result.status === 401) {
        localStorage.removeItem('token')
        Toast.fire({ icon: 'warning', title: result.message })
        navigate('/login')
        return
      }
      if (result.status !== 200) {
        Toast.fire({ icon: 'warning', title: result.message })
        return
      }
      Toast.fire({ icon: 'success', title: 'Your account is all set!' })
      navigate('/dashboard')
    } catch (error) {
      Toast.fire({ icon: 'error', title: 'Something went wrong. Please try again.' })
    } finally {
      setLoader(false)
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="ob-intro">
            <img src="/ollarin-mockup%20(3).png" alt="" className="ob-intro-img" />
            <h2 className="ob-title">Personalise your experience</h2>
            <p className="ob-subtitle">
              We'll ask you a few quick questions to set up your account and suggest the strategy that fits you best.
            </p>
          </div>
        )
      case 2:
        return (
          <fieldset className="ob-fieldset">
            <legend className="ob-title">Have you invested in stocks or cryptocurrency before?</legend>
            <p className="ob-subtitle">Whether you're a seasoned investor or just starting out, tell us about your experience.</p>
            <div className="ob-options">
              {EXPERIENCE_OPTIONS.map(option => (
                <label key={option.value} className={`ob-option ${answers.investmentExperience === option.value ? 'selected' : ''}`}>
                  <input type="radio" name="experience" value={option.value}
                    checked={answers.investmentExperience === option.value}
                    onChange={() => setField('investmentExperience', option.value)} />
                  <span className="ob-indicator radio" />
                  <span className="ob-option-label">{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )
      case 3:
        return (
          <fieldset className="ob-fieldset">
            <legend className="ob-title">Why do you want to invest?</legend>
            <p className="ob-subtitle">Select all that apply.</p>
            <div className="ob-options">
              {GOAL_OPTIONS.map(goal => (
                <label key={goal} className={`ob-option ${answers.investmentGoals.includes(goal) ? 'selected' : ''}`}>
                  <input type="checkbox" checked={answers.investmentGoals.includes(goal)}
                    onChange={() => toggleInList('investmentGoals', goal)} />
                  <span className="ob-indicator checkbox" />
                  <span className="ob-option-label">{goal}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )
      case 4:
        return (
          <fieldset className="ob-fieldset">
            <legend className="ob-title">Which asset classes interest you the most?</legend>
            <p className="ob-subtitle">Choose one or more options.</p>
            <div className="ob-options ob-options-grid">
              {ASSET_OPTIONS.map(asset => (
                <label key={asset} className={`ob-option ${answers.assetInterests.includes(asset) ? 'selected' : ''}`}>
                  <input type="checkbox" checked={answers.assetInterests.includes(asset)}
                    onChange={() => toggleInList('assetInterests', asset)} />
                  <span className="ob-indicator checkbox" />
                  <span className="ob-option-label">{asset}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )
      case 5:
        return (
          <fieldset className="ob-fieldset">
            <legend className="ob-title">What is your estimated net worth?</legend>
            <p className="ob-subtitle">Pick the range that best describes your total assets minus liabilities. This helps us tailor your account.</p>
            <div className="ob-options ob-options-grid">
              {NET_WORTH_OPTIONS.map(range => (
                <label key={range} className={`ob-option ${answers.netWorth === range ? 'selected' : ''}`}>
                  <input type="radio" name="netWorth" value={range}
                    checked={answers.netWorth === range}
                    onChange={() => setField('netWorth', range)} />
                  <span className="ob-indicator radio" />
                  <span className="ob-option-label">{range}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )
      case 6:
        return (
          <fieldset className="ob-fieldset">
            <legend className="ob-title">Choose your account type</legend>
            <p className="ob-subtitle">Pick the option that best describes what you need.</p>
            <div className="ob-options">
              {ACCOUNT_TYPES.map(type => (
                <label key={type.value} className={`ob-option ob-option-rich ${answers.accountType === type.value ? 'selected' : ''}`}>
                  <input type="radio" name="accountType" value={type.value}
                    checked={answers.accountType === type.value}
                    onChange={() => setField('accountType', type.value)} />
                  <span className="ob-indicator radio" />
                  <span>
                    <span className="ob-option-label">{type.label}</span>
                    <span className="ob-option-desc">{type.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )
      case 7:
        return (
          <div>
            <h2 className="ob-title">Complete your account details</h2>
            <p className="ob-subtitle">Set your account currency and confirm your information below.</p>
            <div className="ob-form-grid">
              <div className="ob-field full">
                <label htmlFor="ob-currency">Account currency</label>
                <select id="ob-currency" value={answers.accountCurrency}
                  onChange={(e) => setField('accountCurrency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="ob-field">
                <label htmlFor="ob-firstname">First name</label>
                <input id="ob-firstname" type="text" required value={answers.firstname}
                  onChange={(e) => setField('firstname', e.target.value)} />
              </div>
              <div className="ob-field">
                <label htmlFor="ob-lastname">Last name</label>
                <input id="ob-lastname" type="text" required value={answers.lastname}
                  onChange={(e) => setField('lastname', e.target.value)} />
              </div>
              <div className="ob-field full">
                <label htmlFor="ob-address">Address</label>
                <input id="ob-address" type="text" required autoComplete="street-address" value={answers.address}
                  onChange={(e) => setField('address', e.target.value)} />
              </div>
              <div className="ob-field">
                <label htmlFor="ob-city">City</label>
                <input id="ob-city" type="text" required autoComplete="address-level2" value={answers.city}
                  onChange={(e) => setField('city', e.target.value)} />
              </div>
              <div className="ob-field">
                <label htmlFor="ob-state">State / Region</label>
                <input id="ob-state" type="text" autoComplete="address-level1" value={answers.state}
                  onChange={(e) => setField('state', e.target.value)} />
              </div>
              <div className="ob-field">
                <label htmlFor="ob-zipcode">Zip / Postal code</label>
                <input id="ob-zipcode" type="text" autoComplete="postal-code" value={answers.zipcode}
                  onChange={(e) => setField('zipcode', e.target.value)} />
              </div>
              <div className="ob-field">
                <label htmlFor="ob-country">Country</label>
                <input id="ob-country" type="text" required autoComplete="country-name" value={answers.country}
                  onChange={(e) => setField('country', e.target.value)} />
              </div>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const isLastStep = step === TOTAL_STEPS

  return (
    <main className="signup-page onboarding-page">
      {loader && <Loader />}
      <form className="ob-card" onSubmit={isLastStep ? submit : (e) => { e.preventDefault(); next() }}>
        <div className="ob-progress-row">
          <span className="ob-step-count">{step} / {TOTAL_STEPS}</span>
          <img src="/ollarinmirror-logo.svg" alt="Ollarin Mirror" className="ob-logo" />
        </div>
        <div className="ob-progress" aria-hidden="true">
          <div className="ob-progress-fill" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        <div className={`ob-actions ${step === 1 ? 'center' : ''}`}>
          {step > 1 && (
            <button type="button" className="ob-btn ob-btn-secondary" onClick={previous}>Previous</button>
          )}
          <button type="submit" className="ob-btn ob-btn-primary">
            {step === 1 ? "Let's get started" : isLastStep ? 'Submit' : 'Next'}
          </button>
        </div>
      </form>
    </main>
  )
}

export default Onboarding
